/**
 * Shared helpers for handling Clerk's `needs_client_trust` sign-in status.
 *
 * Client Trust requires a step-up email or phone code on a new device when a
 * password sign-in succeeds. This module centralises the factor lookup and
 * `prepareSecondFactor` invocation so the password and sign-in-controller
 * hooks don't drift out of sync.
 */

import type { SignInResource, SignInSecondFactor } from "@clerk/types";

export type ClientTrustStrategy = Extract<
  SignInSecondFactor["strategy"],
  "email_code" | "phone_code"
>;

export type ClientTrustFactor = Extract<
  SignInSecondFactor,
  { strategy: "email_code" | "phone_code" }
>;

export function findClientTrustFactor(
  signIn: SignInResource,
): ClientTrustFactor | null {
  const factors = signIn.supportedSecondFactors ?? [];
  // Match Clerk's docs example: prefer email_code, fall back to phone_code.
  return (
    factors.find(
      (factor): factor is ClientTrustFactor =>
        Boolean(factor) && factor.strategy === "email_code",
    ) ??
    factors.find(
      (factor): factor is ClientTrustFactor =>
        Boolean(factor) && factor.strategy === "phone_code",
    ) ??
    null
  );
}

export function prepareClientTrustFactor(
  signIn: SignInResource,
  factor: ClientTrustFactor,
): Promise<SignInResource> {
  return factor.strategy === "phone_code"
    ? signIn.prepareSecondFactor({
        strategy: "phone_code",
        phoneNumberId: factor.phoneNumberId,
      })
    : signIn.prepareSecondFactor({
        strategy: "email_code",
        emailAddressId: factor.emailAddressId,
      });
}
